import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { ApiPalletDto } from '../dto/pallet.dto';
import { API_ENDPOINTS } from '../../../core/providers/constants/api.constants';
import { Pallet } from '../types/pallet';
import { PackingBoxDto } from '../dto/packing-box.dto';

@Injectable({
  providedIn: 'root',
})
export class ApiRouterPalletService extends IApiBackendRouteService {
  urlRoute = `${API_ENDPOINTS.PALLETS}`;
  constructor(protected readonly http: HttpClient) {
    super();
  }

  sendableReports(
    typeId: number,
    query?: ApiPalletDto['sendableReports']['query']
  ): Observable<ApiPalletDto['sendableReports']['return']> {
    let queryParams = new HttpParams();
    if (query?.startDate) {
      queryParams = queryParams.set('startDate', query.startDate);
    }
    if (query?.endDate) {
      queryParams = queryParams.set('endDate', query.endDate);
    }
    if (query?.reportType) {
      queryParams = queryParams.set('reportType', query.reportType);
    }
    const includeQuery = queryParams.keys().length > 0;
    return this.http.get<ApiPalletDto['sendableReports']['return']>(
      `${this.urlRoute}/${typeId}/sendable${
        includeQuery ? `?${queryParams.toString()}` : ''
      }`
    );
  }

  infoToDisplay(
    id: number
  ): Observable<ApiPalletDto['infoToDisplay']['return']> {
    return this.http.get<ApiPalletDto['infoToDisplay']['return']>(
      `${this.urlRoute}/${id}/info-to-display`
    );
  }

  allInfoToDisplay(
    startStr?: string,
    endStr?: string,
    department?: string
  ): Observable<ApiPalletDto['infoToDisplay']['return'][]> {
    const params = new HttpParams({
      fromObject: {
        ...(startStr && endStr && { startDate: startStr, endDate: endStr }),
        ...(department && { department }),
      },
    });
    return this.http.get<ApiPalletDto['infoToDisplay']['return'][]>(
      `${this.urlRoute}/info-to-display`,
      {
        params,
      }
    );
  }

  create(body: { teamId: number[] | number }): Observable<Pallet> {
    return this.http.post<Pallet>(`${this.urlRoute}`, body);
  }

  addTeam(palletId: number, body: { teamId: number }): Observable<Pallet> {
    return this.http.patch<Pallet>(
      `${this.urlRoute}/${palletId}/add-team`,
      body
    );
  }

  finishPallet(palletId: number): Observable<any> {
    return this.http.post(`${this.urlRoute}/${palletId}/finish`, {});
  }

  destroyPallet(palletId: number): Observable<any> {
    return this.http.delete(`${this.urlRoute}/${palletId}/destroy`);
  }

  createPalletReport(palletId: number, options?: {}): Observable<any> {
    return this.http.post(`${this.urlRoute}/${palletId}/report`, {
      ...options,
    });
  }

  getPallet(id: number): Observable<Pallet> {
    return this.http.get<Pallet>(`${this.urlRoute}/${id}`);
  }

  traceData(queryString: string): Observable<Pallet[]> {
    return this.http.get<Pallet[]>(`${this.urlRoute}/full?${queryString}`);
  }

  getDetails(id: number): Observable<ApiPalletDto['palletDetail']> {
    return this.http.get<ApiPalletDto['palletDetail']>(
      `${this.urlRoute}/traceability/${id}`
    );
  }

  getOpenPallets(workOrderId: number): Observable<Pallet[]> {
    const apiURL = `${this.urlRoute}/open?workOrderId=${workOrderId}`;
    return this.http.get<Pallet[]>(apiURL);
  }

  getWorkOrderPalletReport(
    queryString: string
  ): Observable<PackingBoxDto['scannedBoxReport']['result']> {
    return this.http.get<PackingBoxDto['scannedBoxReport']['result']>(
      `${this.urlRoute}/traceability/report?${queryString}`
    );
  }

  scannedBoxOnPalletReport(
    queryString: string
  ): Observable<PackingBoxDto['scannedBoxOnPallet']['result']> {
    return this.http.get<PackingBoxDto['scannedBoxOnPallet']['result']>(
      `${this.urlRoute}/traceability/scanner/report?${queryString}`
    );
  }

  getLabelCode128(text: string): Observable<{ data: string }> {
    return this.http.get<{ data: string }>(
      `${this.urlRoute}/label?bcid=code128&text=${text}&scale=1&height=45&padding=4`
    );
  }
}
